<script lang="ts" setup>
import { CalcType } from '@posx/core';
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { defineStorageStore } from '@/utils/storage-helper';
import { notify } from '@/utils/notification';
import { TDiscountItem, DiscountItem } from '@/types/discount.type';
import { formatCurrency, getCurrencySymbol } from '@/utils/formatNumber';

const { t, locale } = useI18n();
const discountStore = defineStorageStore<TDiscountItem>('discount')();


const $emit = defineEmits(['change', 'update:modelValue', 'close', 'confirm']);
const $props = defineProps<{
  modelValue: boolean;
  data: number;
  discountType?: CalcType;
  totalAmount?: number;
}>();

const value = ref('0');
const visible = ref($props.modelValue);

// public discount validation function
const validateDiscount = (discountType: CalcType, discountValue: number): boolean => {
  if (!$props.totalAmount || $props.totalAmount === 0) {
    notify.error(t('discount.error_no_items_to_discount'));
    return false;
  }
  
  console.log({
    'discountType': discountType, 
    'discountValue': discountValue, 
    'totalAmount': $props.totalAmount, 
    '$props.data': $props.data
  });
  // calculate new discount amount
  let newDiscountAmount = discountType === CalcType.Flat ? discountValue : ($props.totalAmount - $props.data) * discountValue;
  
  // validate total discount cannot be greater than total amount
  if (discountType === CalcType.Percent && $props.data + newDiscountAmount > $props.totalAmount) {
    notify.error(t('discount.error_exceed_total'));
    return false;
  }
  
  return true;
};

// get available discounts (filter out the ones that don't match the current discount type)
const availableDiscounts = computed((): TDiscountItem[] => {
  let filteredDiscounts = discountStore.data.filter(discount => 
    discount.calc_type === $props.discountType
  );
  
  if (filteredDiscounts.length < 1) {
    // create default discount objects
    const defaultDiscounts: TDiscountItem[] = [];
    if ($props.discountType === CalcType.Percent) {
      defaultDiscounts.push(
        Object.assign(new DiscountItem(), { 
          name: '10% Off', 
          calc_type: CalcType.Percent, 
          percent: 0.1,
          amount: 0 
        }),
        Object.assign(new DiscountItem(), { 
          name: '15% Off', 
          calc_type: CalcType.Percent, 
          percent: 0.15,
          amount: 0 
        }),
        Object.assign(new DiscountItem(), { 
          name: '20% Off', 
          calc_type: CalcType.Percent, 
          percent: 0.2,
          amount: 0 
        }),
        Object.assign(new DiscountItem(), { 
          name: '25% Off', 
          calc_type: CalcType.Percent, 
          percent: 0.25,
          amount: 0 
        })
      );
    } else {
      defaultDiscounts.push(
        Object.assign(new DiscountItem(), { name: `-${formatCurrency(10)}`, calc_type: CalcType.Flat, amount: 10, percent: 0 }),
        Object.assign(new DiscountItem(), { name: `-${formatCurrency(15)}`, calc_type: CalcType.Flat, amount: 15, percent: 0 }),
        Object.assign(new DiscountItem(), { name: `-${formatCurrency(20)}`, calc_type: CalcType.Flat, amount: 20, percent: 0 }),
        Object.assign(new DiscountItem(), { name: `-${formatCurrency(25)}`, calc_type: CalcType.Flat, amount: 25, percent: 0 })
      );
    }
    return defaultDiscounts;
  }
  
  return filteredDiscounts;
});

watch(
  () => $props.modelValue,
  (newVal: boolean) => {
    visible.value = newVal;
    if (newVal) {
      // Reset number to 0 when the dialog is opened
      value.value = '0';
    }
  },
);

const handleKeyboardChange = (input: string) => {
  let val = value.value;
  if (input === 'backspace') {
    val = val.slice(0, -1);
    if (val == '') {
      val = '0';
    }
    input = '';
    value.value = val;
    return;
  } else if (input === '.') {
    if (val.includes('.')) {
      input = '';
    }
    if (val === '') {
      input = '0.';
    }
  } else if (val === '0' && input !== '.') {
    value.value = input;
    return;
  }
  value.value += input;
};

const handleClose = () => {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
};

const handleComfirmPrice = () => {
  let val = parseFloat(value.value) || 0; // empty/invalid input = no discount
  if ($props.discountType === CalcType.Percent) {
    val = val / 100;
  }
  // Discount cannot be negative
  if (val < 0) {
    val = 0;
  }
  
  // use public validation function
  if (!validateDiscount($props.discountType!, val)) {
    return;
  }
  
  const customDiscount = Object.assign(new DiscountItem(), {
    name: $props.discountType === CalcType.Percent
      ? `${parseFloat((val * 100).toFixed(2))}% Off`
      : `-${formatCurrency(val)}`,
    calc_type: $props.discountType,
    percent: $props.discountType === CalcType.Percent ? val : 0,
    amount: $props.discountType === CalcType.Flat ? val : 0
  });
  
  $emit('confirm', customDiscount);
};

const handleDiscountSelect = (discount: TDiscountItem) => {
  let discountVal = discount.calc_type === CalcType.Percent ? discount.percent : discount.amount;

  // use public validation function
  if (!validateDiscount(discount.calc_type, discountVal)) {
    return;
  }

  $emit('confirm', discount);
  handleClose();
};

onMounted(async () => {
  await discountStore.getAll();
});
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="t('discount.title')"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <div class="w-[355px] flex flex-col justify-between">
      <div>
        <x-input
          :label="discountType == CalcType.Percent ? t('discount.label_percentage') : t('discount.label_flat', { symbol: getCurrencySymbol() })"
          v-model="value"
        />
        <!-- Available Coupons -->
        <div class="mt-[5px]">
          <div class="grid grid-cols-3 gap-2">
            <x-button 
              v-for="discount in availableDiscounts" 
              :key="discount.uid"
              square 
              size="large" 
              type="primary" 
              @click="handleDiscountSelect(discount)"
              class="text-xs"
            >
                <div class="flex flex-col">
                  <span>{{ discount.name_translations?.[locale] || discount.name }}</span>
                  <span class="mt-[15px]">
                    {{ discount.calc_type === CalcType.Percent
                      ? `-${parseFloat((discount.percent * 100).toFixed(2))}%`
                      : `-${formatCurrency(discount.amount)}`
                    }}
                  </span>
                </div>
            </x-button>
          </div>
        </div>
      </div>
      <div>
        <x-keyboard class="mb-[5px]" :single="true" @change="handleKeyboardChange" />
        <x-button type="primary" class="w-full" @click="handleComfirmPrice">{{ t('common.confirm') }}</x-button>
      </div>
    </div>
  </x-dialog>
</template>

<style lang="scss" scoped>
.el-button + .el-button {
  margin-left: 0;
}
</style>
